import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { fromEvaluation } from '../../features/offers/offerBenefits'
import type { PromotionEvaluation } from '../../features/offers/promotionEvaluation'
import { OfferCardContent } from './OfferCard'
import CurrencyDisplay from '../shared/CurrencyDisplay'
import { formatNumberEnglish } from '../../utils'

interface OfferConfirmationModalProps {
  isOpen: boolean
  evaluation: PromotionEvaluation
  payableTotal: number
  priceBeforeDiscount?: number
  onConfirm: () => void
  onCancel: () => void
}

const OfferConfirmationModal: React.FC<OfferConfirmationModalProps> = ({
  isOpen,
  evaluation,
  payableTotal,
  priceBeforeDiscount,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation()
  const isRTL = useSelector((state: RootState) => state.settings.settings.language === 'ar')

  if (!isOpen) return null

  const display = fromEvaluation(evaluation)
  const showPriceBeforeDiscount =
    priceBeforeDiscount != null &&
    !Number.isNaN(priceBeforeDiscount) &&
    priceBeforeDiscount > 0

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div
        className={`relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-3xl bg-white shadow-[0px_24px_48px_rgba(18,18,23,0.08)] p-6 space-y-5 ${isRTL ? 'text-right' : 'text-left'}`}
      >
        <h3 className="text-xl font-semibold text-neutral-900 text-center font-['IBM_Plex_Sans_Arabic']">
          {t('offers.confirmOfferTitle')}
        </h3>

        <OfferCardContent display={display} forceExpanded className="w-full" />

        <div className="space-y-2 py-2 border-t border-gray-200">
          {showPriceBeforeDiscount && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600 font-['IBM_Plex_Sans_Arabic']">
                {t('offers.priceBeforeDiscount')}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-neutral-900">
                  {formatNumberEnglish(priceBeforeDiscount!, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
                <CurrencyDisplay className="w-4 h-4" />
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-base font-semibold text-neutral-900 font-['IBM_Plex_Sans_Arabic']">
              {t('offers.finalPayableTotal')}
            </span>
            <div className="flex items-center gap-1">
              <span className="text-base font-bold text-neutral-900">
                {formatNumberEnglish(payableTotal, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <CurrencyDisplay className="w-4 h-4" />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={onConfirm}
            className="w-full h-12 rounded-xl bg-flutter-button text-white font-semibold font-['IBM_Plex_Sans_Arabic']"
          >
            {t('offers.confirmOrder')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-12 rounded-xl border border-black bg-white text-black font-semibold font-['IBM_Plex_Sans_Arabic']"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default OfferConfirmationModal
